// AI Studio, batch mode (admins only): many templates at once.
//
// The admin types one topic per line, chooses the style, the presenter and (optionally) the
// Templates categories and premium flag. The API writes the plans in the background (after
// customers' generations), also while Toons is closed; this browser builds the templates one after
// the other, and finishes the rest when Toons is opened again. Finished templates are saved as templates and, when categories were chosen,
// published in them straight away. Each template can be opened, made again, or shown / hidden.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAIJobs, RUNNING } from "./AIJobsContext";
import { batchProgress } from "./AIToasts";
import AISizePicker, { lastSize, sizeName } from "./AISizePicker";
import { aiApi } from "../Utils/aiTemplate/aiApi";

// Order of a template's steps: the newer of the server's and this browser's state wins.
const STEP = { queued: 0, planning: 1, planned: 2, building: 3, done: 4, failed: 4, cancelled: 4 };
const stepOf = (j) => (j && STEP[j.status] != null ? STEP[j.status] : -1);

const MAX_TOPICS = 30;

const STYLES = [
  { key: "auto", label: "Auto", icon: "auto_awesome" },
  { key: "corporate", label: "Corporate", icon: "business_center" },
  { key: "playful", label: "Playful", icon: "mood" },
  { key: "bold", label: "Bold", icon: "whatshot" },
  { key: "elegant", label: "Elegant", icon: "spa" },
  { key: "minimal", label: "Minimal", icon: "crop_square" },
];

const thumbUrl = (api, path) => (path ? String(api).replace(/\/$/, "") + "/" + String(path).replace(/^\//, "") : null);

const topicsOf = (text) => {
  const seen = new Set();
  return String(text || "")
    .split(/\r?\n/)
    .map((t) => t.replace(/^[\s\-*•\d.)]+/, "").trim())
    .filter((t) => t.length >= 2 && !seen.has(t.toLowerCase()) && seen.add(t.toLowerCase()));
};

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return mins + " min ago";
  const h = Math.round(mins / 60);
  if (h < 24) return h + " h ago";
  return d.toLocaleDateString();
}

function statusLabel(job) {
  if (job.status === "done") return "Ready";
  if (job.status === "failed") return "Failed";
  if (job.status === "cancelled") return "Cancelled";
  if (job.status === "queued") return "Waiting";
  if (job.status === "planning") return "Writing";
  return "Designing";
}

/** The form (inside the AI Studio header). */
export function AIBatchForm({ api, onStarted }) {
  const { startBatch } = useAIJobs();
  const [text, setText] = useState("");
  const [style, setStyle] = useState("auto");
  const [presenter, setPresenter] = useState(true);
  const [canvasSize, setCanvasSize] = useState(lastSize);
  const [categories, setCategories] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [premium, setPremium] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    let off = false;
    aiApi.categories(api).then((list) => !off && setCategories(list));
    return () => {
      off = true;
    };
  }, [api]);

  const topics = topicsOf(text);
  const tooMany = topics.length > MAX_TOPICS;

  const toggle = (id) => setChosen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev.concat(id)));

  const start = async () => {
    if (!topics.length) {
      setProblem("Type at least one topic, one per line.");
      return;
    }
    if (tooMany) {
      setProblem(`A batch can have up to ${MAX_TOPICS} topics.`);
      return;
    }
    setBusy(true);
    setProblem(null);
    const res = await startBatch({ keywords: topics, style, presenter, canvasSize, categoryIds: chosen, premium, name: name.trim() || null });
    setBusy(false);
    if (!res.status) {
      setProblem(res.message || "The batch could not be started.");
      return;
    }
    setText("");
    setName("");
    if (onStarted) onStarted(res.batch);
  };

  return (
    <div className="ais-batch-form">
      <div className="ais-batch-row">
        <textarea
          className="ais-batch-topics"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"One topic per line, for example:\nDental clinic grand opening\nSummer fashion sale\nReal estate open house\nRamadan Kareem greetings"}
          rows={6}
        />
        <div className="ais-batch-side">
          <div className={"ais-batch-count" + (tooMany ? " is-over" : "")}>
            <strong>{topics.length}</strong> / {MAX_TOPICS} topics
          </div>
          <input
            className="ais-batch-name"
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            placeholder="Batch name (optional)"
          />
          <button className="ais-generate ais-batch-go" onClick={start} disabled={busy || !topics.length || tooMany}>
            {busy ? <span className="ais-spinner light" /> : <span className="material-icons">auto_awesome</span>}
            {topics.length > 1 ? `Generate ${topics.length} templates` : "Generate template"}
          </button>
        </div>
      </div>

      <AISizePicker value={canvasSize} onChange={setCanvasSize} />

      <div className="ais-controls">
        <div className="ais-chips">
          {STYLES.map((s) => (
            <button key={s.key} className={"ais-chip" + (style === s.key ? " is-on" : "")} onClick={() => setStyle(s.key)}>
              <span className="material-icons">{s.icon}</span>
              {s.label}
            </button>
          ))}
        </div>
        <label className="ais-switch">
          <input type="checkbox" checked={presenter} onChange={(e) => setPresenter(e.target.checked)} />
          <span className="ais-switch-track"><span /></span>
          Talking presenter
        </label>
      </div>

      <div className="ais-batch-publish">
        <span className="ais-batch-label">Publish in:</span>
        <div className="ais-chips">
          {categories.map((c) => (
            <button key={c.id} className={"ais-chip" + (chosen.includes(c.id) ? " is-on" : "")} onClick={() => toggle(c.id)}>
              {c.name}
            </button>
          ))}
          {!categories.length && <span className="ais-batch-hint">Loading categories...</span>}
        </div>
        <label className="ais-switch">
          <input type="checkbox" checked={premium} onChange={(e) => setPremium(e.target.checked)} />
          <span className="ais-switch-track"><span /></span>
          Premium
        </label>
      </div>
      <div className="ais-batch-hint">
        {chosen.length
          ? "Templates are published in these categories as soon as each one is ready."
          : "No category chosen: templates are saved but not shown to customers until you publish them."}{" "}
        The AI writes them on the server even when Toons is closed; the scenes are built in Toons one after
        the other, and any left are finished when you open Toons again.
      </div>
      {problem && (
        <div className="ais-problem">
          <span className="material-icons">info</span>
          <span>{problem}</span>
        </div>
      )}
    </div>
  );
}

/** The admin's batches with their templates (below the header). */
export function AIBatchList({ api, refreshKey }) {
  const { jobsById, openProject, openPanel, regenerate, historyVersion } = useAIJobs();
  const [batches, setBatches] = useState(null);
  const [categories, setCategories] = useState([]);
  const [installed, setInstalled] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const res = await aiApi.batches(api, 8);
    if (res.status) {
      setInstalled(res.installed !== false);
      setBatches(res.batches || []);
    } else setBatches([]);
  }, [api]);

  useEffect(() => {
    load();
  }, [load, refreshKey, historyVersion]);

  useEffect(() => {
    let off = false;
    aiApi.categories(api).then((list) => !off && setCategories(list));
    return () => {
      off = true;
    };
  }, [api]);

  // Live state of this session (progress, scene previews) over the server's list.
  const merged = useMemo(
    () =>
      (batches || []).map((b) => {
        const jobs = (b.jobs || []).map((x) => {
          const live = jobsById[x.job.id];
          // This browser's state (progress, scene previews) unless the server is further on (a
          // template this tab is not following, e.g. finished in another tab).
          if (!live || stepOf(x.job) > stepOf(live)) return x;
          const progress = Math.max(Number(live.progress) || 0, stepOf(x.job) === stepOf(live) ? Number(x.job.progress) || 0 : 0);
          return { ...x, job: { ...x.job, ...live, progress } };
        });
        Object.values(jobsById).forEach((j) => {
          if (j.batchId === b.id && !jobs.some((x) => x.job.id === j.id)) jobs.push({ job: j, published: false });
        });
        const done = jobs.filter((x) => x.job.status === "done").length;
        const left = jobs.filter((x) => RUNNING.includes(x.job.status)).length;
        return { ...b, jobs, done, left, pct: batchProgress(jobs.map((x) => x.job)) };
      }),
    [batches, jobsById]
  );

  // Refreshed every few seconds while a batch is running.
  const running = merged.some((b) => b.left > 0);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [running, load]);

  const catName = (id) => (categories.find((c) => c.id === id) || {}).name || "#" + id;

  const publish = async (b, x, on) => {
    if (!x.job.projectId) return;
    setBusyId(x.job.id);
    const res = await aiApi.publish(api, x.job.projectId, on ? { publish: true, categoryIds: b.categoryIds, premium: b.premium } : { publish: false });
    setBusyId(null);
    if (res.status) load();
  };

  const again = async (x) => {
    setBusyId(x.job.id);
    await regenerate(x.job.id);
    setBusyId(null);
    load();
  };

  if (!installed) {
    return (
      <div className="ais-problem ais-problem-light">
        <span className="material-icons">storage</span>
        <span>Batch mode needs Database/006_ai_batch.sql on the server.</span>
      </div>
    );
  }
  if (batches === null) return <div className="ais-muted ais-pad">Loading...</div>;
  if (!merged.length) {
    return (
      <div className="ais-empty">
        <span className="material-icons">dynamic_feed</span>
        <div>Your batches will appear here.</div>
      </div>
    );
  }

  return (
    <div className="ais-batches">
      {merged.map((b) => (
        <div key={b.id} className="ais-batch">
          <div className="ais-batch-head">
            <div>
              <div className="ais-batch-title">{b.name || "Batch " + b.id}</div>
              <div className="ais-muted">
                {when(b.createdAt)} · {sizeName(b.canvasSize)} · {b.style} · {b.presenter ? "presenter" : "no presenter"} ·{" "}
                {b.categoryIds && b.categoryIds.length ? "published in " + b.categoryIds.map(catName).join(", ") : "not published"}
                {b.premium ? " · premium" : ""}
              </div>
            </div>
            <div className="ais-batch-progress">
              <span>
                <strong>{b.done}</strong> of {b.jobs.length} ready{b.left ? ` · ${b.left} in progress · ${b.pct}%` : ""}
              </span>
              <span className="ais-mini-bar"><span style={{ width: Math.max(3, b.pct) + "%" }} /></span>
            </div>
          </div>
          <div className="ais-grid ais-batch-grid">
            {b.jobs.map((x) => {
              const j = x.job;
              const live = RUNNING.includes(j.status);
              const img = (j.previews && j.previews.find(Boolean)) || thumbUrl(api, j.previewImagePath);
              return (
                <div key={j.id} className={"ais-tile is-" + j.status}>
                  <button
                    className="ais-tile-media"
                    onClick={() => (j.status === "done" && j.projectId ? openProject(j) : openPanel(j.id))}
                    title={j.status === "done" ? "Open in editor" : "Show progress"}
                  >
                    {img && (j.status === "done" || j.status === "building") ? (
                      <img src={img} alt="" />
                    ) : live ? (
                      <div className="ais-tile-running">
                        <span className="material-icons ais-spin-slow">auto_awesome</span>
                        <div className="ais-mini-bar"><span style={{ width: Math.max(4, j.progress || 4) + "%" }} /></div>
                      </div>
                    ) : (
                      <div className="ais-tile-off">
                        <span className="material-icons">{j.status === "cancelled" ? "block" : "error_outline"}</span>
                      </div>
                    )}
                    {j.status === "done" && <span className="ais-tile-open"><span className="material-icons">edit</span> Open</span>}
                  </button>
                  <div className="ais-tile-info">
                    <div className="ais-tile-title" title={j.projectName || j.keyword}>{j.projectName || j.keyword}</div>
                    <div className="ais-tile-meta">
                      <span className={"ais-status s-" + j.status}>{statusLabel(j)}</span>
                      {j.status === "done" && (
                        <span className={"ais-status " + (x.published ? "s-live" : "s-hidden")}>{x.published ? "Published" : "Hidden"}</span>
                      )}
                    </div>
                    {j.status === "failed" && j.error && <div className="ais-batch-error" title={j.error}>{j.error}</div>}
                    <div className="ais-batch-actions">
                      {(j.status === "done" || j.status === "failed" || j.status === "cancelled") && (
                        <button className="ais-link" disabled={busyId === j.id} onClick={() => again(x)}>Make again</button>
                      )}
                      {j.status === "done" && x.published && (
                        <button className="ais-link" disabled={busyId === j.id} onClick={() => publish(b, x, false)}>Hide</button>
                      )}
                      {j.status === "done" && !x.published && b.categoryIds && b.categoryIds.length > 0 && (
                        <button className="ais-link" disabled={busyId === j.id} onClick={() => publish(b, x, true)}>Publish</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
